import { type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

type Tone = "neutral" | "primary" | "success" | "warn" | "danger" | "info";
type Variant = "soft" | "solid" | "outline";

interface Props extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  variant?: Variant;
}

const tones: Record<Tone, { soft: string; solid: string; outline: string }> = {
  neutral: {
    soft: "bg-muted text-muted-fg",
    solid: "bg-muted-fg text-bg",
    outline: "border border-border text-fg",
  },
  primary: {
    soft: "bg-primary/12 text-primary",
    solid: "bg-primary text-primary-fg",
    outline: "border border-primary/40 text-primary",
  },
  success: {
    soft: "bg-success/12 text-success",
    solid: "bg-success text-white",
    outline: "border border-success/40 text-success",
  },
  warn: {
    soft: "bg-warn/15 text-warn",
    solid: "bg-warn text-bg",
    outline: "border border-warn/40 text-warn",
  },
  danger: {
    soft: "bg-danger/12 text-danger",
    solid: "bg-danger text-white",
    outline: "border border-danger/40 text-danger",
  },
  info: {
    soft: "bg-accent/12 text-accent",
    solid: "bg-accent text-white",
    outline: "border border-accent/40 text-accent",
  },
};

export function Badge({ className, tone = "neutral", variant = "soft", ...rest }: Props) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
        tones[tone][variant],
        className,
      )}
      {...rest}
    />
  );
}
